import { supabase } from "@/shared/integrations/supabase/client";
import { hasSurveySourceColumn, selectWithSurveySource } from "@/shared/integrations/supabase/surveySource";
import type { PropertyAttributeRow } from "@/features/properties/types/propertyAttributes";
import { buildEmptySurveyData, type TemplateFieldDef } from "./buildEmptySurveyData";

// Use untyped alias since property_attributes isn't in generated types
const db = supabase as unknown as any;

export const NO_TEMPLATE_ERROR = "No survey template configured for this project";
export const DUPLICATE_LEAF_ID_ERROR = "Survey template has duplicate question ids";

/**
 * Build a self-describing prod_data map from the template — one entry per
 * question (value null = unanswered), carrying its own structure
 * (`field_type`/`options`/`section_path`/`order`). New surveys are born in
 * prod_data so they render standalone with no template lookup.
 */
function buildProdDataFromTemplate(
  templateFields: TemplateFieldDef[],
  now: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const counter = { n: 0 };
  const walk = (nodes: TemplateFieldDef[], path: Array<{ id: string; title: string }>) => {
    for (const f of nodes) {
      if (f.type === "node") {
        walk((f.fields as TemplateFieldDef[]) ?? [], [...path, { id: f.id, title: f.title ?? "" }]);
        continue;
      }
      const order = counter.n++;
      // Reject duplicate leaf ids: a collision would overwrite an earlier entry,
      // producing an incomplete survey that we'd then mark prod_data-backed.
      if (Object.prototype.hasOwnProperty.call(out, f.id)) {
        throw new Error(`${DUPLICATE_LEAF_ID_ERROR}: ${f.id}`);
      }
      const options = Array.isArray(f.options)
        ? (f.options as unknown[]).filter((o): o is string => typeof o === "string" && o.length > 0)
        : null;
      out[f.id] = {
        final_survey_answer: null,
        final_source: "surveyor",
        final_tier: "A",
        status: "unanswered",
        review_note: null,
        reviewed_by: "survey-create",
        reviewed_at: now,
        question_id: f.id,
        leaf_id: f.id,
        question_title: f.title ?? null,
        section: path.length ? path[path.length - 1].title : null,
        t_class: null,
        input_type: null,
        field_type: f.type ?? null,
        options: options && options.length ? options : null,
        section_path: path,
        order,
        human_answer: null,
        human_confidence: null,
        ai_answer: null,
        ai_run_label: null,
        reviewer_confidence: null,
        evidence: [],
      };
    }
  };
  walk(templateFields, []);
  return out;
}

/**
 * Creates a new, empty site survey for a property from the project's latest
 * attribute template and returns the inserted row. `captured_at` and
 * `normalization_status` come from column defaults, matching how the Flutter
 * field app creates surveys on upload.
 */
export async function createSurveyFromTemplate(
  projectId: string,
  propertyId: string
): Promise<PropertyAttributeRow> {
  const { data: template, error: templateError } = await db
    .from("attribute_templates")
    .select("schema_definition")
    .eq("organization_id", projectId)
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (templateError) throw templateError;
  if (!template?.schema_definition) throw new Error(NO_TEMPLATE_ERROR);

  const templateFields: TemplateFieldDef[] =
    typeof template.schema_definition === "string"
      ? JSON.parse(template.schema_definition)
      : template.schema_definition;

  const data = buildEmptySurveyData(templateFields);
  // Born in prod_data: self-describing entry per question + flag the render source.
  const prod_data = buildProdDataFromTemplate(templateFields, new Date().toISOString());

  // Set survey_source only when the column exists (migration-safe). Without it,
  // inserting the field would 400; the survey still renders from `data` (also
  // written here), and is born prod_data-native once the migration is applied.
  const hasSurveySource = await hasSurveySourceColumn();
  const insertRow = hasSurveySource
    ? { property_id: propertyId, data, prod_data, survey_source: "prod_data" }
    : { property_id: propertyId, data, prod_data };
  const { data: row, error: insertError } = await db
    .from("property_attributes")
    .insert(insertRow)
    .select(
      await selectWithSurveySource(
        "id, property_id, captured_at, data, prod_data, normalization_status, processing_events, last_attempt_at, attempt_count, ref_id"
      )
    )
    .single();

  if (insertError) throw insertError;
  return row as PropertyAttributeRow;
}
